import { useMemo, useState } from "react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import type { CountryCodeOption } from "../constants/country-codes";
import { countryCodes } from "../constants/country-codes";

interface CountryCodeDrawerProps {
  onSelect: (country: CountryCodeOption) => void;
  selectedDialCode?: string;
  selectedIso?: string;
}

export default function CountryCodeDrawer({
  onSelect,
  selectedDialCode,
  selectedIso,
}: Readonly<CountryCodeDrawerProps>) {
  const [search, setSearch] = useState("");

  const normalizedTerm = search
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const numericTerm = search.replace(/[^0-9]/g, "");

  const filtered = useMemo(() => {
    const tokens = normalizedTerm.split(/\s+/).filter(Boolean);
    if (!normalizedTerm && !numericTerm) {
      return countryCodes;
    }
    return countryCodes.filter((country) => {
      const nameNormalized = country.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      const codeNormalized = country.code.toLowerCase();
      const dialNormalized = country.dialCode.toLowerCase();
      const dialNumeric = country.dialCode.replace(/[^0-9]/g, "");
      const initials = country.name
        .split(/\s+/)
        .map((part) => part[0] ?? "")
        .join("")
        .toLowerCase();

      const tokensMatch =
        tokens.length === 0 ||
        tokens.every(
          (token) =>
            nameNormalized.includes(token) || codeNormalized.includes(token)
        );

      const dialMatch =
        normalizedTerm.length > 0 && dialNormalized.includes(normalizedTerm);
      const numericMatch =
        numericTerm.length > 0 && dialNumeric.includes(numericTerm);
      const initialsMatch =
        normalizedTerm.length > 0 && initials.includes(normalizedTerm);

      return tokensMatch || dialMatch || numericMatch || initialsMatch;
    });
  }, [normalizedTerm, numericTerm]);

  return (
    <div className="flex h-full flex-col gap-4 text-white">
      <Input
        autoFocus
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by country or code"
        className="bg-main-dark border-border text-sm"
      />
      <div className="flex-1 overflow-y-auto pr-2">
        {filtered.length === 0 ? (
          <p className="text-sm text-white/50">No countries found.</p>
        ) : (
          <ul className="space-y-1">
            {filtered.map((country) => {
              const isSelected = selectedIso
                ? selectedIso === country.code
                : selectedDialCode === country.dialCode;

              return (
                <li key={country.code}>
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn(
                      "w-full justify-start gap-3 rounded-lg px-3 py-2 text-left text-sm",
                      isSelected
                        ? "bg-white/10 hover:bg-white/10"
                        : "hover:bg-white/5"
                    )}
                    onClick={() => onSelect(country)}>
                    <span className="text-lg">{country.flag}</span>
                    <span className="flex-1 text-left">
                      <span className="block font-medium text-white">
                        {country.name}
                      </span>
                      <span className="text-xs text-white/60">
                        {country.code} · {country.dialCode}
                      </span>
                    </span>
                    <span className="font-semibold text-white/80">
                      {country.dialCode}
                    </span>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
